import React, { FC, useEffect, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Container, Tab, Tabs, Typography } from '@material-ui/core';
import cx from 'classnames';
import differenceInBusinessDays from 'date-fns/differenceInBusinessDays';
import formatISO9075 from 'date-fns/formatISO9075';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import cpdLogo from '../../../../assets/images/cpd-logo.png';
import { BarChart } from '../../../../components/BarChart';
import { CertificateListItem } from '../../../../components/CertificateListItem';
import { CustomAccordion } from '../../../../components/CustomAccordion';
import { LineChart } from '../../../../components/LineChart';
import { LoadingOverlay } from '../../../../components/LoadingOverlay';
import { ProgressBar } from '../../../../components/ProgressBar';
import { RadarChart } from '../../../../components/RadarChart';
import RolesViewSwitcher from '../../../../components/RolesViewSwitcher';
import CardWrapper from '../../../../components/UI/CardWrapper/CardWrapper';
import { VideoListItem } from '../../../../components/VideoListItem';
import {
  GET_ANALYTICS,
  GET_PATHWAYS_CERTIFICATES,
  GET_PATHWAYS_STATS,
  GET_USER_VIDEOS_PROGRESS_STAT,
} from '../../../../graphql/analytics';
import { GET_WATCHLIST } from '../../../../graphql/dashboard';
import {
  getUserAnalytics,
  getUserAnalyticsArgs,
  GroupByPeriod,
  ListDashboardArgs,
  Query,
  QueryGetPathwayCertificatesArgs,
  QueryGetUserPathwaysProgressStatArgs,
  TypeAnalytic,
  UserVideosProgressStatArgs,
  UserVideosProgressStatData,
} from '../../../../types';
import {
  generateContinueWatchingLink,
  getQueryStringParams,
  getVideoProgressDataByCompleted,
  secondsToHms,
} from '../../../../utils/helpers';
import { usePathwayStatAndProgressValue } from '../../../../utils/usePathwaysProgressAndValue';
import { Header } from '../../../DiscoverPage/components/Header';
import { DEFAULT_URL } from '../../../DiscoverPage/const';
import { dateFilterOptions, initialDatesUserFilter } from '../../const';
import {
  getDateFilters,
  getSingleUserFilter,
  transformUserVideosCategoryData,
  transformUserVideosWatchedData,
  transformUserViewingHoursData,
} from '../../helpers';
import useStyles from '../../styles';
import Card from '../Card';
import PickerWithRanges from '../PickerWithRanges';
import TabPanel, { a11yProps } from '../TabPanel/TabPanel';

interface UserProps {
  isMobile?: boolean;
  userId: string;
  userName?: string | null;
  isManager: boolean;
  tenantTitle?: string | null;
  tenantName?: string | null;
  hasSubscription?: boolean;
}

const dateFilters = getDateFilters();

const User: FC<UserProps> = ({
  isMobile,
  userId,
  userName,
  isManager,
  tenantTitle,
  tenantName,
  hasSubscription = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const location = useLocation();
  const search = location?.search;
  const params = getQueryStringParams(search);

  const [tabIndex, setTabIndex] = useState(0);
  const [videosTableTab, setVideosTableTab] = useState(0);
  const [selectedTimeRange, setTimeRange] = useState(initialDatesUserFilter);

  const filtersWrapRef = useRef<HTMLDivElement>(null);
  const filtersWrapEl = filtersWrapRef?.current;
  const selectedTimeRangeValue = selectedTimeRange.value;

  const [singleUserFilter, setSingleUserFilter] = useState(
    getSingleUserFilter(userId, dateFilters[selectedTimeRangeValue]),
  );

  useEffect(() => {
    setSingleUserFilter(getSingleUserFilter(userId, dateFilters[selectedTimeRangeValue]));
  }, [userId, selectedTimeRangeValue]);

  const { data, loading, error } = useQuery<getUserAnalytics, getUserAnalyticsArgs>(GET_ANALYTICS, {
    variables: {
      statisticFilter: {
        user_ids: [parseInt(userId)],
        date: singleUserFilter.date,
        type: TypeAnalytic.User,
      },
      viewedHoursFilter: { ...singleUserFilter, type: TypeAnalytic.User },
      viewedVideosFilter: { ...singleUserFilter, type: TypeAnalytic.User },
      loginStatFilter: singleUserFilter,
    },
    fetchPolicy: 'network-only',
  });

  const { data: videosProgressData, loading: loadingVideosProgressData } = useQuery<
    UserVideosProgressStatData,
    UserVideosProgressStatArgs
  >(GET_USER_VIDEOS_PROGRESS_STAT, {
    variables: {
      filter: {
        user_ids: [parseInt(userId)],
        date: singleUserFilter.date,
        type: TypeAnalytic.User,
      },
    },
    fetchPolicy: 'network-only',
  });

  const { data: watchlistData, loading: loadingWatchlist } = useQuery<
    Pick<Query, 'GetWatchList'>,
    ListDashboardArgs
  >(GET_WATCHLIST, {
    variables: {
      user_id: userId,
    },
    fetchPolicy: 'network-only',
  });

  const { data: pathwaysStatData, loading: loadingPathwaysStat } = useQuery<
    Pick<Query, 'getUserPathwaysProgressStat'>,
    QueryGetUserPathwaysProgressStatArgs
  >(GET_PATHWAYS_STATS, {
    variables: {
      filter: {
        type: TypeAnalytic.User,
        user_ids: [Number(userId)],
        date: singleUserFilter.date,
      },
    },
    fetchPolicy: 'network-only',
  });

  const { data: pathwayCertificatesData, loading: loadingPathwaysCertificates } = useQuery<
    Pick<Query, 'getPathwayCertificates'>,
    QueryGetPathwayCertificatesArgs
  >(GET_PATHWAYS_CERTIFICATES, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        date: singleUserFilter.date,
      },
    },
  });

  const pathwaysStat = pathwaysStatData?.getUserPathwaysProgressStat;
  const certificatesStat = pathwayCertificatesData?.getPathwayCertificates || [];

  const viewingHoursData = data?.ViewedHoursStat;
  const videosWatchedData = data?.VideosWatchedCountStat;
  const videosWatchedByCategoryData = data?.VideosWatchedByCathegoryStat;
  const videosProgressStat = videosProgressData?.UserVideosProgressStat;
  const listOfAreas = data?.listBaseVideoCategories;
  const viewingHoursDataGrByDates = viewingHoursData?.grouped_by_dates;
  const viewingHoursSum = viewingHoursData?.total_view_duration;
  const watchlistVideos = watchlistData?.GetWatchList || [];

  const videosProgressesByUserVideos = videosProgressStat?.videos_progresses;
  const preparedVideosProgressData = videosProgressesByUserVideos
    ? videosProgressesByUserVideos.filter((item) => item?.video)
    : [];

  const videosWatchedDataGrByDates = videosWatchedData?.grouped_by_dates;

  const preparedViewingHoursData = transformUserViewingHoursData(viewingHoursDataGrByDates);
  const preparedVideosWatchedData = videosWatchedDataGrByDates
    ? transformUserVideosWatchedData(videosWatchedDataGrByDates)
    : [];
  const preparedVideosCategoryData = listOfAreas
    ? transformUserVideosCategoryData(listOfAreas, videosWatchedByCategoryData, userName)
    : [];

  const handleChangeIndex = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleChangeVideosTable = (event, newValue) => {
    setVideosTableTab(newValue);
  };

  const changeTimeRange = (option) => {
    if (!option) return;
    setTimeRange(option);
  };

  const [pathwaysValue, pathwaysProgressValue] = usePathwayStatAndProgressValue(
    pathwaysStatData?.getUserPathwaysProgressStat?.pathways_stat,
  );

  if (error) {
    return (
      <div>
        {t('error.error')}: {error.message}
      </div>
    );
  }

  const pickerSubmitHandler = (from, to) => {
    let groupBy = GroupByPeriod.Month;
    const diff = differenceInBusinessDays(to, from);

    if (diff < 90) groupBy = GroupByPeriod.Week;
    if (diff < 30) groupBy = GroupByPeriod.Day;

    setSingleUserFilter({
      user_ids: userId,
      date: {
        from: formatISO9075(from),
        to: formatISO9075(to),
      },
      group_by: groupBy,
    });
  };

  return (
    <div className={classes.wrapper}>
      <Header
        title={t('page.analytics.heading')}
        wrapperClass={classes.headerWrapper}
        contentWrapperClass={classes.headerContainer}
        titleClass={classes.headerTitle}
        tenantName={tenantName}
      >
        <Container>
          <Typography className={classes.headerDescription}>
            {t('page.analytics.userDesc')}
          </Typography>
        </Container>
      </Header>
      {isManager && hasSubscription && (
        <Container className={classes.viewSwitcherWrap}>
          <RolesViewSwitcher
            rightBtnLink="/analytics"
            leftBtnLink="/analytics/dashboard"
            wrapperClass={classes.viewSwitcher}
          />
        </Container>
      )}
      <Container className={cx(classes.container, classes.userAnalytics)}>
        <Helmet>
          <title>
            {t('page.analytics.heading')} - {tenantTitle}
          </title>
        </Helmet>

        <div className={classes.selectWrapper}>
          <div className={classes.selectFiltersWrapper} ref={filtersWrapRef}>
            <PickerWithRanges
              id="range-picker"
              anchorEl={filtersWrapEl}
              options={dateFilterOptions}
              value={selectedTimeRange}
              onChange={changeTimeRange}
              onSubmit={pickerSubmitHandler}
              isMobile={isMobile}
            />
          </div>
        </div>

        <div className={classes.innerWrapper}>
          <div className={classes.smallCardsWrap}>
            <Card
              title={t('page.analytics.viewingHours')}
              value={viewingHoursSum}
              toggle={false}
              type={null}
              activeType={null}
              onClick={null}
              loading={loading}
            >
              <LineChart data={preparedViewingHoursData} />
            </Card>

            <Card
              title={t('page.analytics.videoesWatched')}
              value={videosWatchedData?.total_count_viewed_videos}
              toggle={false}
              type={null}
              activeType={null}
              onClick={null}
              loading={loading}
            >
              <BarChart data={preparedVideosWatchedData} />
            </Card>

            <Card
              title={t('page.analytics.pathwaysCompleted')}
              value={pathwaysValue}
              toggle={false}
              type={null}
              activeType={null}
              onClick={null}
              loading={loadingPathwaysStat}
              chart={false}
            >
              <div className={classes.progressBarWrapper}>
                {Boolean(pathwaysProgressValue || pathwaysProgressValue === 0) && (
                  <ProgressBar value={pathwaysProgressValue} />
                )}
              </div>
            </Card>

            <Card
              title={t('page.analytics.cpdCredits')}
              value={pathwaysStat?.cpd_credits_stat?.completed}
              toggle={false}
              type={null}
              activeType={null}
              onClick={null}
              loading={loadingPathwaysStat}
              chart={false}
            >
              <img src={cpdLogo} alt="CPD certification" />
            </Card>
          </div>

          <Card
            className={classes.radarCard}
            title={t('page.analytics.financeProfile')}
            value={null}
            toggle={false}
            type={null}
            activeType={null}
            onClick={null}
            loading={loading}
          >
            <div className={classes.radarWrapper}>
              <RadarChart data={preparedVideosCategoryData} keys={[userName]} />
            </div>
          </Card>
        </div>
        <CardWrapper wrapperClass={classes.tableWrapper}>
          <div className={classes.anchor} id="pathways" />
          <Tabs
            value={tabIndex}
            onChange={handleChangeIndex}
            textColor="inherit"
            aria-label="pathways tabs"
          >
            <Tab label={t('page.analytics.myPathways')} {...a11yProps(0)} />
            <Tab
              data-testid="myCertificatesTab"
              label={t('page.analytics.myCertificates')}
              {...a11yProps(1)}
            />
          </Tabs>

          <TabPanel value={tabIndex} index={0}>
            {!pathwaysStat?.pathways_progresses_stat?.length ? (
              <div className={classes.tableEmptyState}>
                {loadingPathwaysStat ? (
                  <>{t('common.loading')}...</>
                ) : (
                  <>
                    {t('page.analytics.noPathways')}{' '}
                    <Link to="/pathways">{t('page.analytics.firstPathway')}</Link>.
                  </>
                )}
              </div>
            ) : (
              <CustomAccordion
                itemsToShow={isMobile ? 3 : 5}
                wrapperClass={classes.tabMargin}
                buttonClass={classes.accordionBtn}
              >
                {pathwaysStat?.pathways_progresses_stat?.map((item) => {
                  const progressValue = item?.progress || 0;
                  const slug = item?.pathway?.slug;

                  return (
                    <VideoListItem
                      key={item?.pathway?.id}
                      wrapperClass={classes.tableItem}
                      url={`/pathway/${slug}/`}
                      title={item?.pathway?.name}
                      imageUrl={item?.pathway?.image_url}
                      current={item?.pathway_watched_videos_count || 0}
                      total={item?.pathway_videos_count || 0}
                      progress={progressValue}
                      pattern={(current, total) =>
                        `${current} / ${total} ${t('page.analytics.videosSm')}`
                      }
                      testScore={item?.test_score || 0}
                      pathwayStatus={item?.status}
                      hasQuizzes={Boolean(item?.pathway?.quizes?.length)}
                    />
                  );
                })}
              </CustomAccordion>
            )}
          </TabPanel>

          <TabPanel value={tabIndex} index={1}>
            {!certificatesStat?.length ? (
              <div className={classes.tableEmptyState}>
                {loadingPathwaysCertificates ? (
                  <>{t('common.loading')}...</>
                ) : (
                  <>{t('page.analytics.noCertificates')}</>
                )}
              </div>
            ) : (
              <CustomAccordion
                itemsToShow={isMobile ? 3 : 5}
                wrapperClass={classes.tabMargin}
                buttonClass={classes.accordionBtn}
              >
                {certificatesStat.map((item) => (
                  <CertificateListItem
                    key={item?.pathway?.id}
                    wrapperClass={classes.tableItem}
                    slug={item?.pathway?.slug}
                    title={item?.pathway?.name}
                    imageUrl={item?.pathway?.image_url}
                    cpdCredits={item?.pathway?.cpd_credits}
                    category={item?.pathway?.categories?.[0]?.title}
                    hash={item?.hash}
                    tenantName={tenantName}
                    tenantTitle={tenantTitle}
                    passDate={item?.date_passing}
                  />
                ))}
              </CustomAccordion>
            )}
          </TabPanel>

          {loading && <LoadingOverlay borderRadius={5} />}
        </CardWrapper>
        <CardWrapper wrapperClass={classes.tableWrapper}>
          <div className={classes.anchor} id="videos" />
          <Tabs
            value={videosTableTab}
            onChange={handleChangeVideosTable}
            textColor="inherit"
            aria-label="videos tabs"
          >
            <Tab label={t('page.analytics.myVideos')} {...a11yProps(0)} />
            <Tab label={t('page.analytics.myWatchlist')} {...a11yProps(1)} />
          </Tabs>

          <TabPanel value={videosTableTab} index={0}>
            {preparedVideosProgressData?.length === 0 ? (
              <div className={classes.tableEmptyState}>
                {loadingVideosProgressData ? (
                  <>{t('common.loading')}...</>
                ) : (
                  <>
                    {t('page.analytics.noVideos')}{' '}
                    <Link to={`${DEFAULT_URL}/videos`}>{t('page.analytics.firstVideo')}</Link>.
                  </>
                )}
              </div>
            ) : (
              <CustomAccordion
                itemsToShow={isMobile ? 3 : 5}
                wrapperClass={classes.tabMargin}
                buttonClass={classes.accordionBtn}
              >
                {preparedVideosProgressData &&
                  preparedVideosProgressData.map((item) => {
                    const duration = item?.video?.duration; // in seconds
                    const { progress } = getVideoProgressDataByCompleted(
                      item?.last_position,
                      duration,
                      item?.completed,
                      params?.debug === '1',
                    );
                    const videoUrl = generateContinueWatchingLink(
                      `/${item?.video?.video_url}`,
                      item?.chapter,
                      item?.time,
                    );
                    return (
                      <VideoListItem
                        key={item?.video?.id}
                        title={item?.video?.title}
                        imageUrl={item?.video?.preview_url}
                        url={videoUrl}
                        current={secondsToHms(progress)}
                        total={secondsToHms(duration)}
                        progress={Math.round(item?.progress_percentage || 0)}
                        withPlayIcon={true}
                        wrapperClass={classes.tableItem}
                      />
                    );
                  })}
              </CustomAccordion>
            )}
          </TabPanel>
          <TabPanel value={videosTableTab} index={1}>
            {watchlistVideos?.length === 0 ? (
              <div className={classes.tableEmptyState}>
                {loadingWatchlist ? (
                  <>{t('common.loading')}...</>
                ) : (
                  <>{t('page.analytics.noWatchList')}</>
                )}
              </div>
            ) : (
              <CustomAccordion
                itemsToShow={isMobile ? 3 : 5}
                wrapperClass={classes.tabMargin}
                buttonClass={classes.accordionBtn}
              >
                {watchlistVideos.map((item) => {
                  const duration = item?.video?.duration;
                  const { progress } = getVideoProgressDataByCompleted(
                    item?.last_position,
                    duration,
                    item?.completed,
                    params?.debug === '1',
                  );
                  const url = generateContinueWatchingLink(
                    `/${item?.video?.video_url}`,
                    item?.chapter,
                    item?.time,
                  );
                  return (
                    <VideoListItem
                      key={item?.video?.id}
                      title={item?.video?.title}
                      imageUrl={item?.video?.preview_url}
                      url={url}
                      current={secondsToHms(progress)}
                      total={secondsToHms(duration)}
                      progress={Math.round(item?.progress_percentage || 0)}
                      withPlayIcon={true}
                    />
                  );
                })}
              </CustomAccordion>
            )}
          </TabPanel>
          {loading && <LoadingOverlay borderRadius={5} />}
        </CardWrapper>
      </Container>
    </div>
  );
};

export default User;
